import Image from 'next/image';
import type { CSSProperties } from 'react';
import type { ImageType } from '../../../types.d';
import { Button } from '../../ui/button/Button';
import { Shimmer, toBase64 } from '../../utility/imageShimmer/ImageShimmer';
import styles from './TryShadeCard.module.scss';

export type TryShadeCardProps = {
  title?: string;
  tagline?: string;
  // productType used to get card product icons.
  productType?: string;
  productHandle?: string;
  productImage?: ImageType;
  icons: Array<ImageType>;
  ctaTitle?: string;
  backgroundColor?: string;
  textTheme: 'light' | 'dark';
};

/**
 * Try Shade Card component.
 * @param props - component props.
 */
export const TryShadeCard = ({
  productType,
  tagline,
  productHandle,
  productImage,
  icons,
  ctaTitle,
  backgroundColor,
  textTheme,
  isLoading,
}: TryShadeCardProps & { isLoading?: boolean }) => {
  if (isLoading) {
    return (
      <div
        data-testid="loading"
        className={[styles.tryShadeCard, 'is-loading'].join(' ')}
      ></div>
    );
  }

  return (
    <div className={styles.tryShadeCard}>
      <div
        className={styles.tryShadeCard__details}
        style={{ '--background-color': backgroundColor } as CSSProperties}
      >
        {productImage?.url && (
          <Image
            className={styles.tryShadeCard__image}
            src={productImage.url}
            alt={productImage.title}
            title={productImage.title}
            height="185"
            width="145"
            placeholder="blur"
            blurDataURL={`data:image/svg+xml;base64,${toBase64(
              Shimmer(productImage.width, productImage.height)
            )}`}
          />
        )}
        <div>
          {tagline && (
            <p
              className={[
                styles.tryShadeCard__tagline,
                'text-utility-utility-small',
              ].join(' ')}
            >
              {tagline}
            </p>
          )}

          {productType && (
            <h5
              className={[
                styles.tryShadeCard__title,
                textTheme === 'light' && styles['tryShadeCard__title--light'],
                'text-heading-h2-mobile text-heading-h2-desktop',
              ].join(' ')}
            >
              {productType}
            </h5>
          )}

          {productHandle && ctaTitle && (
            <Button
              type="button"
              modifiers="text"
              className={[
                styles.tryShadeCard__link,
                textTheme === 'light' && styles['tryShadeCard__link--light'],
                'text-body-2-bold-mobile text-body-2-bold-desktop',
              ].join(' ')}
              href={`/products/${productHandle}`}
            >
              <span>{ctaTitle}</span>
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tryShadeCard__footer}>
        <div className={styles.tryShadeCard__icons}>
          {icons.length > 0 &&
            icons.map((icon, index) => {
              return (
                <div key={index} className={styles.tryShadeCard__icon}>
                  <Image
                    className={styles.tryShadeCard__image}
                    src={icon.url}
                    alt={icon.title}
                    title={icon.title}
                    height={icon.height}
                    width={icon.width}
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      Shimmer(icon.width, icon.height)
                    )}`}
                  />

                  <p
                    className={[
                      styles.tryShadeCard__iconTitle,
                      'text-blog-body-caption-mobile',
                    ].join(' ')}
                  >
                    {icon.title}
                  </p>
                </div>
              );
            })}
        </div>

        {productHandle && ctaTitle && (
          <Button
            type="button"
            className={[styles.tryShadeCard__cta, 'text-utility-utility'].join(
              ' '
            )}
            href={`/products/${productHandle}`}
          >
            <span>{ctaTitle}</span>
          </Button>
        )}
      </div>
    </div>
  );
};
